// @ts-nocheck
/**
 * Tenant visibility & sign-in details
 *
 * Lets a superadmin (any tenant) or a school admin (their own tenant) toggle:
 *   • which role portals are visible on this tenant (parent / student /
 *     school_admin / merchant)
 *   • which sub-modules in the School Admin dashboard are hidden
 *     (health / transport / merchants / procurement / finance)
 *   • sample login credentials shown on the tenant's sign-in page
 *
 * Persists via the `update_tenant_visibility` RPC (SECURITY DEFINER,
 * authorized by `can_manage_tenant_branding`).
 */
import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Loader2, Save, Plus, Trash2, Eye, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { useTenantOverride } from "@/lib/tenant-override";
import {
  fetchTenantBranding,
  ALL_PORTAL_KEYS,
  TOGGLEABLE_MODULE_KEYS,
  type TenantBranding,
  type PortalKey,
  type DemoCredential,
} from "@/lib/tenant-branding";

const PORTAL_LABELS: Record<PortalKey, string> = {
  parent: "Parents portal",
  student: "Students portal",
  school_admin: "School Admin portal",
  merchant: "Merchant portal",
};

const MODULE_LABELS: Record<(typeof TOGGLEABLE_MODULE_KEYS)[number], string> = {
  health: "Health & Care",
  transport: "Transport",
  merchants: "Merchants & POS",
  procurement: "Procurement",
  finance: "Finance",
};

export function TenantVisibilitySettings() {
  const { tenantSlug: userSlug, isSuperadmin } = useAuth();
  const { activeTenant, overrideActive } = useTenantOverride();
  const editingSlug = overrideActive ? activeTenant?.slug ?? null : userSlug;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [row, setRow] = useState<TenantBranding | null>(null);
  const [enabledPortals, setEnabledPortals] = useState<PortalKey[]>([...ALL_PORTAL_KEYS]);
  const [disabledModules, setDisabledModules] = useState<string[]>([]);
  const [creds, setCreds] = useState<DemoCredential[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      if (!editingSlug) {
        if (!cancelled) { setRow(null); setLoading(false); }
        return;
      }
      const r = await fetchTenantBranding(editingSlug);
      if (cancelled) return;
      setRow(r);
      setEnabledPortals(
        Array.isArray(r?.enabled_portals) && r!.enabled_portals!.length
          ? (r!.enabled_portals as PortalKey[])
          : [...ALL_PORTAL_KEYS],
      );
      setDisabledModules(Array.isArray(r?.disabled_modules) ? r!.disabled_modules! : []);
      setCreds(Array.isArray(r?.demo_credentials) ? r!.demo_credentials! : []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [editingSlug]);

  const canEdit = useMemo(() => !!editingSlug, [editingSlug]);

  function togglePortal(k: PortalKey, on: boolean) {
    setEnabledPortals((prev) => {
      const set = new Set(prev);
      if (on) set.add(k); else set.delete(k);
      return ALL_PORTAL_KEYS.filter((p) => set.has(p));
    });
  }
  function toggleModule(k: string, hidden: boolean) {
    setDisabledModules((prev) => {
      const set = new Set(prev);
      if (hidden) set.add(k); else set.delete(k);
      return Array.from(set);
    });
  }

  async function save() {
    if (!editingSlug) return;
    setSaving(true);
    const { error } = await (supabase as any).rpc("update_tenant_visibility", {
      _slug: editingSlug,
      _enabled_portals: enabledPortals,
      _disabled_modules: disabledModules,
      _demo_credentials: creds,
    });
    setSaving(false);
    if (error) { toast.error("Save failed", { description: error.message }); return; }
    toast.success("Visibility & sign-in details updated");
    const fresh = await fetchTenantBranding(editingSlug);
    setRow(fresh);
  }

  if (!editingSlug) {
    return (
      <Card className="p-6 text-sm text-muted-foreground">
        Sign in as a tenant user (or switch into a tenant as superadmin) to manage visibility.
      </Card>
    );
  }
  if (loading) {
    return <Card className="grid h-40 place-items-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></Card>;
  }

  const brandName = row?.name ?? editingSlug;

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between border-b p-4">
        <div>
          <div className="flex items-center gap-2 font-display text-lg leading-none">
            <Eye className="h-4 w-4" /> Module visibility & sign-in details
          </div>
          <div className="mt-1 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
            Editing <span className="font-medium text-foreground">{brandName}</span>
            {isSuperadmin && overrideActive && (
              <Badge variant="outline" className="ml-2 text-[9px]">Superadmin override</Badge>
            )}
          </div>
        </div>
        <Button size="sm" disabled={saving || !canEdit} onClick={save}>
          {saving ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-2 h-3.5 w-3.5" />}
          Save
        </Button>
      </div>

      <div className="grid gap-6 p-4 lg:grid-cols-2">
        {/* Portal visibility */}
        <section className="space-y-3">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Role portals</div>
          <p className="text-[12px] text-muted-foreground">
            When a portal is off, users of that role signing into <b>{brandName}</b> see a "portal disabled" notice
            instead of the dashboard. Superadmins always bypass this gate.
          </p>
          <div className="rounded-lg border">
            {ALL_PORTAL_KEYS.map((k, i) => {
              const on = enabledPortals.includes(k);
              return (
                <div key={k} className={`flex items-center justify-between p-3 ${i > 0 ? "border-t" : ""}`}>
                  <div>
                    <div className="text-sm font-medium">{PORTAL_LABELS[k]}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {on ? "Visible on this tenant" : "Hidden — users see a disabled notice"}
                    </div>
                  </div>
                  <Switch checked={on} onCheckedChange={(v) => togglePortal(k, v)} />
                </div>
              );
            })}
          </div>
        </section>

        {/* Module visibility */}
        <section className="space-y-3">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">School Admin modules</div>
          <p className="text-[12px] text-muted-foreground">
            Hide sub-modules from the School Admin sidebar. Hidden modules disappear from navigation for
            everyone on this tenant.
          </p>
          <div className="rounded-lg border">
            {TOGGLEABLE_MODULE_KEYS.map((k, i) => {
              const hidden = disabledModules.includes(k);
              return (
                <div key={k} className={`flex items-center justify-between p-3 ${i > 0 ? "border-t" : ""}`}>
                  <div>
                    <div className="text-sm font-medium">{MODULE_LABELS[k]}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {hidden ? "Hidden from sidebar" : "Visible in School Admin"}
                    </div>
                  </div>
                  <Switch checked={!hidden} onCheckedChange={(v) => toggleModule(k, !v)} />
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {/* Demo credentials */}
      <div className="border-t p-4">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <KeyRound className="h-3.5 w-3.5" /> Sign-in details shown on {brandName} login page
          </div>
          <Button size="sm" variant="outline" onClick={() => setCreds((c) => [...c, { label: "", email: "", password: "", role: "parent" }])}>
            <Plus className="mr-1 h-3.5 w-3.5" /> Add credential
          </Button>
        </div>
        <p className="mb-3 text-[12px] text-muted-foreground">
          These appear as click-to-fill buttons on the tenant's <code>/auth</code> page. Anyone visiting can
          see them — use for demo accounts only, never real staff or family credentials.
        </p>
        {creds.length === 0 ? (
          <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
            No demo credentials yet. Add one to have it appear on the tenant sign-in page.
          </div>
        ) : (
          <div className="space-y-2">
            {creds.map((c, idx) => (
              <div key={idx} className="grid gap-2 rounded-lg border p-3 md:grid-cols-[1.2fr_1.4fr_1fr_1fr_auto]">
                <div>
                  <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Label</Label>
                  <Input value={c.label} onChange={(e) => setCreds((all) => all.map((x, i) => i === idx ? { ...x, label: e.target.value } : x))} placeholder="Head Teacher" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Email</Label>
                  <Input value={c.email} onChange={(e) => setCreds((all) => all.map((x, i) => i === idx ? { ...x, email: e.target.value } : x))} placeholder="admin@fundred.demo" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Password</Label>
                  <Input value={c.password} onChange={(e) => setCreds((all) => all.map((x, i) => i === idx ? { ...x, password: e.target.value } : x))} placeholder="Demo1234!" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Role</Label>
                  <Input value={c.role ?? ""} onChange={(e) => setCreds((all) => all.map((x, i) => i === idx ? { ...x, role: e.target.value } : x))} placeholder="parent" />
                </div>
                <div className="flex items-end">
                  <Button variant="ghost" size="icon" onClick={() => setCreds((all) => all.filter((_, i) => i !== idx))} aria-label="Remove credential">
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
